import get from 'lodash/get';
import { createSelector } from 'reselect';

import { NODE_TYPE_ROOT, NODE_TYPE_GROUP } from 'common/entities/formNode/nodeTypes';
import { getFormNodes, getFormNodeByName } from 'common/entities/formNode/selectors';
import { getFormSessionStepsEnabled } from 'common/entities/form/sessionRegistration';
import { NODE_NAME_ATTENDING } from 'common/entities/formNode/nodeNames';

import reducerNamespace from '../constants/reducerNamespace';

const nodeTypesToExclude = [NODE_TYPE_ROOT, NODE_TYPE_GROUP];

export const getFormState = (state) => get(state, [reducerNamespace, 'form']);

// Default
export const getFormDefault = createSelector(getFormState, (formState) => get(formState, 'default'));

export const getFormSubmitting = createSelector(getFormDefault, (formDefault) => get(formDefault, 'isSubmitting'));

export const getFormValues = createSelector(getFormDefault, (formDefault) => get(formDefault, 'values'));

export const getFormInitialValues = createSelector(getFormDefault, (formDefault) => get(formDefault, 'initialValues'));

export const getFormErrors = createSelector(getFormDefault, (formDefault) => get(formDefault, 'errors'));

export const getFormMeta = createSelector(getFormDefault, (formDefault) => get(formDefault, 'meta'));

export const getFormTouched = createSelector(getFormDefault, (formDefault) => get(formDefault, 'touched'));

export const getFormVisibility = createSelector(getFormDefault, (formDefault) => get(formDefault, 'visibility'));

export const getFormNodesVisibility = (state, groupId) => {
    const formState = getFormState(state);
    return get(formState, `${groupId}.visibility`, {});
};

export const getVisibleGroupFormNodes = createSelector(
    (state, formId, groupId) => getFormNodes(state, formId),
    (state, formId, groupId) => (groupId ? getFormNodesVisibility(state, groupId) : getFormVisibility(state)),
    (formNodes, formNodeVisibility) =>
        formNodes
            .filter((node) => formNodeVisibility[node.id])
            .filter((node) => !nodeTypesToExclude.includes(node.type)),
);

export const getFormValue = (state, { id }) => getFormValues(state)[id];
export const getFormInitialValue = (state, { id }) => getFormInitialValues(state)[id];
export const getFormError = (state, { id }) => getFormErrors(state)[id];
export const getFormMetaFromId = (state, { id }) => getFormMeta(state)[id];
export const getFormNodeTouched = (state, { id }) => getFormTouched(state)[id];
export const getFormNodeValue = (state, groupId) => get(getFormState(state), `[${groupId}].values`);
export const getFormNodeVisibility = (state, { id, groupId }) => {
    const formState = getFormState(state);

    const groupVisibility = groupId && formState[groupId] && formState[groupId].visibility;

    return groupVisibility ? groupVisibility[id] : getFormVisibility(state)[id];
};

export const getTicketSelectionQuantityValues = createSelector(getFormState, (formValues) =>
    get(formValues, ['quantity', 'values'], {}),
);

export const getTicketTypeQuantityValue = (state, { id }) => getTicketSelectionQuantityValues(state)[id];

export const getTicketSelectionOpenPriceValues = createSelector(getFormState, (formValues) =>
    get(formValues, ['openPrice', 'values'], {}),
);

export const getTicketTypeOpenPriceValue = (state, { id }) => getTicketSelectionOpenPriceValues(state)[id];

// Groups
export const getAllFormValues = (state) => {
    const formGroups = getFormState(state) || {};
    const defaultValues = getFormValues(state);

    return Object.keys(formGroups).reduce(
        (prev, next) => ({
            ...prev,
            [next]: formGroups[next].values || {},
        }),
        defaultValues,
    );
};
export const getFormGroupValues = (state, { group }) => get(getFormState(state), [group, 'values']);
export const getFormGroupValue = (state, { id, group }) => get(getFormState(state), [group, 'values', id]);
export const getFormGroupInitialValue = (state, { id, group }) =>
    get(getFormState(state), [group, 'initialValues', id]);
export const getFormGroupError = (state, { id, group }) => get(getFormState(state), [group, 'errors', id]);

export const getSelectedSessions = (state) => {
    const formDefault = getFormDefault(state);
    return get(formDefault, 'sessions', {});
};

export const getSelectedSessionIds = createSelector(
    [getSelectedSessions, getFormSessionStepsEnabled],
    (sessionsMap, sessionStepsEnabled) => {
        if (!sessionStepsEnabled) {
            return undefined;
        }

        return Object.keys(sessionsMap).filter((id) => sessionsMap[id]);
    },
);

export const getAttendingNodeValue = (state, formId) => {
    const { id } = getFormNodeByName(state, formId, NODE_NAME_ATTENDING) || {};

    if (!id) return null;
    return getFormValue(state, { id });
};
